import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { logger } from '../../utils/logger';
import ResponsiveModal from '../common/ResponsiveModal';
import GradientText from '../common/GradientText';
import {
  Sun,
  X,
  Check,
  Sparkles,
  Plus,
  ArrowRight,
  Trash2,
  ChevronDown,
} from '../common/icons';
import {
  DailyBrief,
  AdjustmentResultData,
  TaskSuggestion
} from '../../types/morningKickoff';
import { TimeBox } from '../../types/timeBox';
import { Event } from '../../types/calendar-event';
import { DailyScheduleSection } from './DailyScheduleSection';
import { AdjustmentInput } from './AdjustmentInput';
import { AdjustmentPreview } from './AdjustmentPreview';

type ModalView = 'reading' | 'adjusting' | 'preview';

type SuggestionActionState = 'loading' | 'error';

interface SuggestionUI {
  actionState?: SuggestionActionState;
  error?: string;
  expanded: boolean;
  overrideTime?: string;
  timePickerOpen: boolean;
}

const SUGGESTION_TYPE_CONFIG = {
  create: {
    Icon: Plus,
    iconClass: 'text-emerald-600 bg-emerald-50',
    actionLabel: 'Accept',
    actionClass: 'btn-accent',
  },
  move: {
    Icon: ArrowRight,
    iconClass: 'text-sky-600 bg-sky-50',
    actionLabel: 'Accept',
    actionClass: 'btn-accent',
  },
  delete: {
    Icon: Trash2,
    iconClass: 'text-red-500 bg-red-50',
    actionLabel: 'Remove',
    actionClass: 'btn-danger-outline',
  },
} as const;

const getSuggestionKey = (s: TaskSuggestion) => s.taskId ?? s.itemId ?? s.taskName;

/** 計算今日可用時段（07:00–22:00，30 分鐘為最小單位） */
function findAvailableSlots(
  busyIntervals: Array<{ start: number; end: number }>,
  durationMinutes: number,
): string[] {
  const slots: string[] = [];
  for (let start = 7 * 60; start + durationMinutes <= 22 * 60; start += 30) {
    const end = start + durationMinutes;
    if (!busyIntervals.some(b => b.start < end && b.end > start)) {
      const h = String(Math.floor(start / 60)).padStart(2, '0');
      const m = String(start % 60).padStart(2, '0');
      slots.push(`${h}:${m}`);
    }
  }
  return slots;
}

interface MorningBriefModalProps {
  isOpen: boolean;
  onClose: () => void;
  briefData: DailyBrief | null;
  isLoading: boolean;
  onConfirmStartDay: () => void;
  onAdjustSchedule: (adjustmentText: string) => Promise<AdjustmentResultData | null>;
  onApplyAdjustment: () => Promise<void>;
  onReloadBrief: () => Promise<void>;
  timeBoxes?: TimeBox[];
  events?: Event[];
  onSuggestionAccept: (suggestion: TaskSuggestion) => Promise<void>;
  acceptedSuggestionKeys?: string[];
}

// 動態 Loading 文字組件
const LoadingAnimatedText: React.FC = () => {
  const [currentText, setCurrentText] = React.useState(0);

  const loadingTexts = [
    'Brewing your morning brief',
    'Reviewing your priorities',
    'Checking for conflicts',
    'Optimizing your schedule',
    'Adding final touches',
    'Almost ready'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentText(prev => (prev + 1) % loadingTexts.length);
    }, 1500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="text-center py-4">
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-700">
        <Sun
          size={16}
          className="animate-spin"
          style={{ animationDuration: '3s' }}
        />
        <span className="text-sm font-medium animate-pulse">
          {loadingTexts[currentText]}...
        </span>
      </div>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="relative min-h-full p-5 md:p-6 space-y-5">
    <LoadingAnimatedText />
    <div className="h-6 bg-orange-200/60 rounded w-3/4 animate-pulse" />
    <div className="space-y-3">
      <div className="h-4 bg-orange-200/50 rounded w-1/3 animate-pulse" />
      <div className="space-y-4">
        <div className="h-16 bg-orange-200/40 rounded animate-pulse" />
        <div className="h-16 bg-orange-200/40 rounded animate-pulse" />
        <div className="h-16 bg-orange-200/40 rounded animate-pulse" />
      </div>
    </div>
  </div>
);

interface SuggestionsSectionProps {
  suggestions: TaskSuggestion[];
  overallReason?: string | null;
  acceptedSuggestionKeys: string[];
  suggestionUI: Record<string, SuggestionUI>;
  busyIntervals: Array<{ start: number; end: number }>;
  onAccept: (suggestion: TaskSuggestion) => Promise<void>;
  onUIUpdate: (key: string, patch: Partial<SuggestionUI>) => void;
}

const SuggestionsSection: React.FC<SuggestionsSectionProps> = ({
  suggestions,
  overallReason,
  acceptedSuggestionKeys,
  suggestionUI,
  busyIntervals,
  onAccept,
  onUIUpdate,
}) => {
  if (suggestions.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <h3 className="text-base md:text-sm font-semibold text-orange-700 uppercase tracking-wide flex items-center gap-1.5">
        <Sparkles size={14} strokeWidth={2} />
        Today's Suggestions
      </h3>
      {overallReason && (
        <p className="text-sm text-gray-500 leading-snug">{overallReason}</p>
      )}

      <div className="divide-y divide-gray-100 rounded-xl overflow-hidden bg-white shadow-sm">
        {suggestions.map((suggestion) => {
          const key = getSuggestionKey(suggestion);
          const ui = suggestionUI[key] ?? { expanded: false, timePickerOpen: false };
          const isAccepted = acceptedSuggestionKeys.includes(key);
          const isActionLoading = ui.actionState === 'loading';
          const config = SUGGESTION_TYPE_CONFIG[suggestion.type] ?? SUGGESTION_TYPE_CONFIG.create;
          const { Icon, iconClass, actionLabel, actionClass } = config;

          const effectiveTime = suggestion.type === 'create'
            ? (ui.overrideTime ?? suggestion.suggestedTime)
            : undefined;

          const timeLabel = (() => {
            if (suggestion.type === 'create' && effectiveTime && suggestion.durationMinutes) {
              const [h, m] = effectiveTime.split(':').map(Number);
              const endTotal = h * 60 + m + suggestion.durationMinutes;
              const endH = String(Math.floor(endTotal / 60) % 24).padStart(2, '0');
              const endM = String(endTotal % 60).padStart(2, '0');
              return `${effectiveTime} - ${endH}:${endM}`;
            }
            if (suggestion.type === 'move' && suggestion.fromTime && suggestion.toTime) {
              return `${suggestion.fromTime} → ${suggestion.toTime}`;
            }
            return null;
          })();

          const availableSlots = suggestion.type === 'create' && suggestion.durationMinutes
            ? findAvailableSlots(busyIntervals, suggestion.durationMinutes)
            : [];

          return (
            <div key={key} className={`transition-opacity ${isAccepted ? 'opacity-40' : ''}`}>
              {/* Main row */}
              <div className="flex items-center gap-3 px-4 py-3">
                <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${iconClass}`}>
                  {isAccepted
                    ? <Check size={14} strokeWidth={2.5} className="text-emerald-600" />
                    : <Icon size={14} strokeWidth={2} />
                  }
                </div>

                <button
                  onClick={() => !isAccepted && onUIUpdate(key, { expanded: !ui.expanded })}
                  disabled={isAccepted}
                  className="flex-1 min-w-0 text-left"
                  aria-expanded={ui.expanded}
                >
                  <span className="flex items-center gap-1">
                    <span className={`text-sm font-medium ${isAccepted ? 'text-gray-400' : 'text-gray-900'}`}>
                      {suggestion.taskName}
                    </span>
                    {!isAccepted && (
                      <ChevronDown size={13} strokeWidth={2} className={`shrink-0 text-gray-300 transition-transform ${ui.expanded ? 'rotate-180' : ''}`} />
                    )}
                  </span>
                  {!isAccepted && timeLabel && (
                    <span
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (availableSlots.length > 0) {
                          onUIUpdate(key, { timePickerOpen: !ui.timePickerOpen });
                        }
                      }}
                      className={`mt-0.5 text-xs inline-flex items-center gap-0.5 ${availableSlots.length > 0 ? 'text-orange-500 hover:text-orange-700' : 'text-gray-400'}`}
                      aria-label="Change time"
                    >
                      {timeLabel}
                      {availableSlots.length > 0 && (
                        <ChevronDown size={11} strokeWidth={2} className={`transition-transform ${ui.timePickerOpen ? 'rotate-180' : ''}`} />
                      )}
                    </span>
                  )}
                </button>

                {isAccepted ? (
                  <span className="text-xs text-emerald-600 shrink-0 font-medium">Done</span>
                ) : (
                  <button
                    onClick={() => onAccept({ ...suggestion, suggestedTime: effectiveTime ?? suggestion.suggestedTime })}
                    disabled={isActionLoading}
                    className={`${actionClass} text-xs px-3 py-1.5 shrink-0 disabled:opacity-50`}
                  >
                    {isActionLoading ? '...' : actionLabel}
                  </button>
                )}
              </div>

              {/* Time picker */}
              {ui.timePickerOpen && !isAccepted && (
                <div className="px-4 pb-3 flex gap-3">
                  <div className="w-7 shrink-0" />
                  <div className="flex flex-wrap gap-1.5">
                    {availableSlots.map(slot => (
                      <button
                        key={slot}
                        onClick={() => onUIUpdate(key, { overrideTime: slot, timePickerOpen: false })}
                        className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                          slot === effectiveTime
                            ? 'bg-sky-500 text-white border-sky-500'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300 hover:text-sky-600'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Expanded reason */}
              {ui.expanded && !isAccepted && (
                <div className="px-4 pb-3 flex items-start gap-3">
                  <div className="w-7 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 leading-relaxed">{suggestion.reason}</p>
                    {ui.error && (
                      <p className="text-xs text-red-500 mt-1 leading-relaxed">{ui.error}</p>
                    )}
                  </div>
                </div>
              )}
              {/* Error shown even when collapsed */}
              {ui.error && !ui.expanded && (
                <div className="px-4 pb-2 flex items-start gap-3">
                  <div className="w-7 shrink-0" />
                  <p className="text-xs text-red-500">{ui.error}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const MorningBriefModal: React.FC<MorningBriefModalProps> = ({
  isOpen,
  onClose,
  briefData,
  isLoading,
  onConfirmStartDay,
  onAdjustSchedule,
  onApplyAdjustment,
  onReloadBrief,
  timeBoxes,
  events,
  onSuggestionAccept,
  acceptedSuggestionKeys = [],
}) => {
  const [currentView, setCurrentView] = useState<ModalView>('reading');
  const [adjustmentData, setAdjustmentData] = useState<AdjustmentResultData | null>(null);
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [suggestionUI, setSuggestionUI] = useState<Record<string, SuggestionUI>>({});

  // 預先計算 busy intervals，避免每個 suggestion 重複 parse
  const busyIntervals = useMemo(() => {
    const intervals: Array<{ start: number; end: number }> = [];
    [...(timeBoxes || []), ...(events || [])].forEach(item => {
      const s = item.startTime instanceof Date ? item.startTime : new Date(item.startTime);
      const e = item.endTime instanceof Date ? item.endTime : new Date(item.endTime);
      intervals.push({ start: s.getHours() * 60 + s.getMinutes(), end: e.getHours() * 60 + e.getMinutes() });
    });
    return intervals;
  }, [timeBoxes, events]);

  const updateSuggestionUI = useCallback((key: string, patch: Partial<SuggestionUI>) => {
    setSuggestionUI(prev => ({
      ...prev,
      [key]: { ...{ expanded: false, timePickerOpen: false }, ...prev[key], ...patch },
    }));
  }, []);

  const handleSuggestionAccept = useCallback(async (suggestion: TaskSuggestion) => {
    const key = getSuggestionKey(suggestion);
    updateSuggestionUI(key, { actionState: 'loading', error: undefined });
    try {
      await onSuggestionAccept(suggestion);
      updateSuggestionUI(key, { actionState: undefined });
    } catch (err) {
      updateSuggestionUI(key, {
        actionState: 'error',
        error: err instanceof Error ? err.message : 'Failed to apply',
      });
    }
  }, [onSuggestionAccept, updateSuggestionUI]);

  const handleStartDay = () => {
    onConfirmStartDay();
    onClose();
  };

  const handleNeedAdjust = () => {
    setCurrentView('adjusting');
  };

  const handleSubmitAdjustment = async (adjustmentText: string) => {
    setIsAdjusting(true);
    try {
      const result = await onAdjustSchedule(adjustmentText);
      if (result) {
        setAdjustmentData(result);
        setCurrentView('preview');
      }
    } catch (error) {
      logger.error('MorningBriefModal', 'Failed to parse adjustment', error);
    } finally {
      setIsAdjusting(false);
    }
  };

  const handleCancelAdjustment = () => {
    setCurrentView('reading');
    setAdjustmentData(null);
  };

  const handleApplyChanges = async () => {
    setIsApplying(true);
    try {
      await onApplyAdjustment();
      await onReloadBrief();
      setCurrentView('reading');
      setAdjustmentData(null);
    } catch (error) {
      logger.error('MorningBriefModal', 'Failed to apply adjustment', error);
    } finally {
      setIsApplying(false);
    }
  };

  const headerContent = (
    <div className="flex items-center gap-2">
      <Sun size={20} className="text-orange-600" strokeWidth={2} />
      <GradientText className="text-lg md:text-xl font-semibold">
        Morning Brief
      </GradientText>
    </div>
  );

  const leftAction = (
    <button
      onClick={onClose}
      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      aria-label="Close"
    >
      <X size={20} />
    </button>
  );

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      showHeader={true}
      headerContent={headerContent}
      leftAction={leftAction}
    >
      <div className="flex flex-col h-full">
        {/* ========== 載入狀態 ========== */}
        {isLoading && (
          <>
            <div className="flex-1 overflow-y-auto hide-scrollbar bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
              <LoadingSkeleton />
            </div>
            <div className="border-t border-gray-100 px-5 py-4 bg-white shrink-0">
              <div className="flex gap-3">
                <div className="flex-1 h-11 bg-gray-200 rounded-lg animate-pulse" />
                <div className="flex-1 h-11 bg-gray-200 rounded-lg animate-pulse" />
              </div>
            </div>
          </>
        )}

        {/* ========== 內容狀態 - 有數據 ========== */}
        {!isLoading && briefData && (
          <>
            {/* ========== State 1: Reading ========== */}
            {currentView === 'reading' && (
              <>
                <div className="flex-1 overflow-y-auto hide-scrollbar bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
                  <div className="relative p-5 md:p-6 space-y-5">
                    {/* Section 1: Your Day Ahead */}
                    {briefData.narrative ? (
                      <div className="space-y-4">
                        <h3 className="text-base md:text-sm font-semibold text-orange-700 uppercase tracking-wide">
                          Your Day Ahead
                        </h3>
                        <p className="text-lg md:text-base leading-relaxed text-gray-800 whitespace-pre-line">
                          {briefData.narrative}
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-8 space-y-3">
                        <div className="w-16 h-16 mx-auto rounded-full bg-orange-100 flex items-center justify-center">
                          <Sun size={32} className="text-orange-600" strokeWidth={2} />
                        </div>
                        <p className="text-gray-800 text-lg font-medium">
                          A Fresh Start
                        </p>
                        <p className="text-gray-600 text-sm">
                          No schedule for today. Ready to create your plan?
                        </p>
                      </div>
                    )}

                    {/* Section 2: Today's Suggestions */}
                    <SuggestionsSection
                      suggestions={briefData.taskSuggestions ?? []}
                      overallReason={briefData.suggestionOverallReason}
                      acceptedSuggestionKeys={acceptedSuggestionKeys}
                      suggestionUI={suggestionUI}
                      busyIntervals={busyIntervals}
                      onAccept={handleSuggestionAccept}
                      onUIUpdate={updateSuggestionUI}
                    />

                    {/* Section 3: Daily Schedule */}
                    <DailyScheduleSection
                      timeBoxes={timeBoxes}
                      events={events}
                    />
                  </div>
                </div>

                <div className="border-t border-gray-100 px-5 py-4 bg-white shrink-0">
                  <div className="flex gap-3">
                    <button
                      onClick={handleNeedAdjust}
                      className="flex-1 btn-secondary text-base md:text-sm"
                    >
                      Adjust
                    </button>
                    <button
                      onClick={handleStartDay}
                      className="flex-1 btn-primary text-base md:text-sm"
                    >
                      Start Day
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ========== State 2: Adjusting ========== */}
            {currentView === 'adjusting' && (
              <div className="flex-1 overflow-y-auto hide-scrollbar bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
                <div className="p-5 md:p-6">
                  <AdjustmentInput
                    onSubmit={handleSubmitAdjustment}
                    onCancel={handleCancelAdjustment}
                    isLoading={isAdjusting}
                  />
                </div>
              </div>
            )}

            {/* ========== State 3: Preview ========== */}
            {currentView === 'preview' && adjustmentData && (
              <div className="flex-1 overflow-y-auto hide-scrollbar bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
                <div className="p-5 md:p-6">
                  <AdjustmentPreview
                    data={adjustmentData}
                    onApply={handleApplyChanges}
                    onCancel={handleCancelAdjustment}
                    isApplying={isApplying}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* ========== 空狀態 ========== */}
        {!isLoading && !briefData && (
          <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-6">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 mx-auto rounded-full bg-orange-100 flex items-center justify-center">
                <Sun size={32} className="text-orange-600" strokeWidth={2} />
              </div>
              <p className="text-gray-600 text-base">
                Unable to load your morning brief.
              </p>
              <p className="text-gray-500 text-sm">
                Please close and try again.
              </p>
            </div>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
};
